import React from 'react'
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api'
import Typography from '@mui/material/Typography'
import { Box, Chip, Grid, Stack, Tooltip } from '@mui/material'
import { IPGeolocationItem, IPGeolocationProxyItem, SearchResult } from '../../store/searcher/reducer'
import { prepareIpGeolocationResult, prepareIpGeolocationResultProxy, prepareNameResult } from '../../store/searcher/service'
import { ResultNotVerified, ResultTitle } from '../../style/result'
import BreachDetectiveResultsViewer from './BreachDetectorViewer'

type Props = {
  apiData: SearchResult
}

export function IPGeolocationCommon(props: Props) {
  const zoom = 5
  const result = prepareIpGeolocationResult(props.apiData)

  const resultProxy = prepareIpGeolocationResultProxy(props.apiData)

  const containerStyle = {
    width: '450px',
    height: '450px',
  }

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
  })

  const [map, setMap] = React.useState(null)

  const onLoad = React.useCallback(function callback(map) {
    if (result.data && Array.isArray(result.data) && result.data.length > 0) {
      result.data.map(el => {
        const bounds = new window.google.maps.LatLngBounds({ lat: el.latitude, lng: el.longitude })
        map.fitBounds(bounds)
      },
      )
    }
    if (resultProxy.data && Array.isArray(resultProxy.data) && resultProxy.data.length > 0) {
      resultProxy.data.map(el => {
        const bounds = new window.google.maps.LatLngBounds({ lat: el.latitude, lng: el.longitude })
        map.fitBounds(bounds)
      },
      )
    }
    setMap(map)
  }, [])

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null)
  }, [])

  const noresults = (
    <Stack>
      <div style={{
        position: 'absolute', left: '50%', top: '50%',
        transform: 'translate(-50%, -50%)',
      }}>
        <ResultNotVerified>Results not found</ResultNotVerified>
      </div>
    </Stack>
  )

  type OutputChipProps = {
    value: string;
  };

  const OutputChip: React.FC<OutputChipProps> = ({ value }) => {
    const maxLength = 20
    const isLong = value.length > maxLength
    const label = isLong ? `${value.slice(0, maxLength)}...` : value

    return (
      <Stack direction="column" spacing={1}>
        <Tooltip title={isLong ? value : ''} arrow>
          <Chip
            label={label}
            size="small"
            color="primary"
            sx={{ cursor: isLong ? 'pointer' : 'default' }}
          />
        </Tooltip>
      </Stack>
    )
  }


  const data = prepareNameResult(props.apiData)

  const breachDetectiveResults = (data?.data ?? [])
    .filter((item: any) => ['breach_ip'].includes(item?.from))
    .flatMap((item: any) => item ?? [])

  return (
    <>
      <Stack>
        {
          isLoaded && result && result.data && result.data.length > 0 && result.data?.map((el: IPGeolocationItem, idx: number) => {
            if (el.latitude && el.longitude) {
              return (
                <Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={5}>
                      <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={{ lat: el.latitude, lng: el.longitude }}
                        zoom={zoom}
                        options={{ minZoom: 3, maxZoom: 12, clickableIcons: false }}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                      >
                        <MarkerF
                          position={{
                            lat: el.latitude,
                            lng: el.longitude,
                          }}
                        />
                      </GoogleMap>
                    </Grid>

                    <Grid
                      item
                      xs={7}
                    >
                      <Grid container spacing={1}>
                        {el.country && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500} >Country:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.country} />
                            </Grid>
                          </>
                        )}
                        {el.city && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500} >City:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.city} />
                            </Grid>
                          </>
                        )}
                        {el.continent && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Continent:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.continent} />
                            </Grid>
                          </>
                        )}
                        {el.region && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Region:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.region} />
                            </Grid>
                          </>
                        )}
                        {el.postal_code && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Postal code:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.postal_code.toString()} />
                            </Grid>
                          </>
                        )}
                        {el.is_vpn !== null && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>VPN:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.is_vpn.toString()} />
                            </Grid>
                          </>
                        )}
                        {el.country_geoname_id && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Country geoname id:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.country_geoname_id.toString()} />
                            </Grid>
                          </>
                        )}
                        {el.city_geoname_id && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>City geoname id:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.city_geoname_id.toString()} />
                            </Grid>
                          </>
                        )}
                        {el.region_geoname_id && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Region geoname id:</Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <OutputChip value={el.region_geoname_id.toString()} />
                            </Grid>
                          </>
                        )}
                        {el.continent_geoname_id && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Continent geoname id:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.continent_geoname_id.toString()} />
                            </Grid>
                          </>
                        )}
                        {el.region_iso_code && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Region ISO code:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.region_iso_code} />
                            </Grid>
                          </>
                        )}
                        {el.country_code && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Country code:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.country_code} />
                            </Grid>
                          </>
                        )}
                        {el.country_is_eu && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Country in EU:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.country_is_eu.toString()} />
                            </Grid>
                          </>
                        )}
                        {el.continent_code && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Continent code:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.continent_code.toString()} />
                            </Grid>
                          </>
                        )}
                        {el.currency_code && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Currency code:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.currency_code} />
                            </Grid>
                          </>
                        )}
                        {el.currency_name && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Currency name:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.currency_name} />
                            </Grid>
                          </>
                        )}
                        {el.flag && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Flag:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.flag} />
                            </Grid>
                          </>
                        )}
                        {el.timezone && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Timezone:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.timezone} />
                            </Grid>
                          </>
                        )}
                        {el.provider && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>Provider:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.provider} />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Stack>
              )
            }
            // } else {
            //   return noresults
            // }
          })
        }
      </Stack>

      <Stack>
        {
          isLoaded && resultProxy && resultProxy.data && resultProxy.data.length > 0 && resultProxy.data?.map((el: IPGeolocationProxyItem, idx: number) => {
            if (el.latitude && el.longitude) {
              return (
                <Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={5}>
                      <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={{ lat: el.latitude, lng: el.longitude }}
                        zoom={zoom}
                        options={{ minZoom: 3, maxZoom: 12, clickableIcons: false }}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                      >
                        <MarkerF
                          position={{
                            lat: el.latitude,
                            lng: el.longitude,
                          }}
                        />
                      </GoogleMap>
                    </Grid>

                    <Grid
                      item
                      xs={7}
                    >
                      <Grid container spacing={1}>
                        {el.asn && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500} >asn:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.asn} />
                            </Grid>
                          </>
                        )}
                        {el.range && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500} >range:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.range} />
                            </Grid>
                          </>
                        )}
                        {el.provider && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>provider:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.provider} />
                            </Grid>
                          </>
                        )}
                        {el.organisation && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>organisation:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.organisation} />
                            </Grid>
                          </>
                        )}
                        {el.continent && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>continent:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.continent} />
                            </Grid>
                          </>
                        )}
                        {el.continentcode && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>continent code:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.continentcode} />
                            </Grid>
                          </>
                        )}
                        {el.country && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>country:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.country} />
                            </Grid>
                          </>
                        )}
                        {el.region && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>region:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.region} />
                            </Grid>
                          </>
                        )}
                        {el.isocode && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>country code:</Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <OutputChip value={el.isocode} />
                            </Grid>
                          </>
                        )}
                        {el.regioncode && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>regioncode code:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.regioncode} />
                            </Grid>
                          </>
                        )}
                        {el.timezone && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>timezone:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.timezone} />
                            </Grid>
                          </>
                        )}
                        {el.city && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>city:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.city} />
                            </Grid>
                          </>
                        )}
                        {el.postcode && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>postcode:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.postcode} />
                            </Grid>
                          </>
                        )}
                        {el.latitude && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>latitude:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.latitude.toString()} />
                            </Grid>
                          </>
                        )}
                        {el.longitude && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>longitude:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.longitude.toString()} />
                            </Grid>
                          </>
                        )}
                        {el.currency?.code && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>currency code:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.currency?.code} />
                            </Grid>
                          </>
                        )}
                        {el.currency?.name && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>currency name:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.currency?.name} />
                            </Grid>
                          </>
                        )}
                        {el.currency?.symbol && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>currency symbol:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.currency?.symbol} />
                            </Grid>
                          </>
                        )}
                        {el.devices?.address && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>devices address:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.devices?.address} />
                            </Grid>
                          </>
                        )}
                        {el.devices?.subnet && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>devices subnet:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.devices?.subnet} />
                            </Grid>
                          </>
                        )}
                        {el.proxy && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>proxy:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.proxy} />
                            </Grid>
                          </>
                        )}
                        {el.type && (
                          <>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="body1" fontWeight={500}>type:</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <OutputChip value={el.type} />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Stack>
              )
            }
            // } else {
            //   return noresults
            // }
          })
        }
      </Stack>
      {breachDetectiveResults.length > 0 && (
        <BreachDetectiveResultsViewer
          breachDetectiveResults={breachDetectiveResults}
        />
      )}
      {resultProxy && resultProxy.data && resultProxy.data.length === 0 && result && result.data && result.data.length === 0 && breachDetectiveResults.length === 0 && noresults}
    </>
  )
}
